import Flex from 'components/common/Flex';
import { ChatContext } from 'context/Context';
import { useContext, useEffect, useState } from 'react';
import { Card, Spinner, Tab } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getBranchOfficesByUserId } from 'redux/branchOfficeSlice';
import {
  getConversation,
  getConversations,
  readMessages,
  sendMessage
} from 'redux/chatSlice';
import { getRealEstateByUserId } from 'redux/realEstateSlice';
import ChatProvider from './ChatProvider';
import ChatContent from './content/ChatContent';
import ChatSidebar from './sidebar/ChatSidebar';
import { useLocation } from 'react-router-dom';

const ChatTab = ({ cid }) => {
  const {
    setIsOpenConversationInfo,
    setActiveConversationId,
    activeConversationId
  } = useContext(ChatContext);
  const [hideSidebar, setHideSidebar] = useState(true);

  const handleSelect = e => {
    setHideSidebar(false);
    setIsOpenConversationInfo(false);
    setActiveConversationId(parseInt(e));
  };

  useEffect(() => {
    if (cid) {
      handleSelect(cid);
    }
  }, [cid]);

  return (
    <Tab.Container activeKey={activeConversationId} onSelect={handleSelect}>
      <Card className="card-chat overflow-hidden bg-white">
        <Card.Body as={Flex} className="p-0 h-100">
          <ChatSidebar
            hideSidebar={hideSidebar}
            setHideSidebar={setHideSidebar}
          />
          <ChatContent setHideSidebar={setHideSidebar} />
        </Card.Body>
      </Card>
    </Tab.Container>
  );
};

const Chat = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cid = searchParams.get('cid');

  const { currentUser: userLogged, socket } = useSelector(state => state.login);
  const branchOffices = useSelector(state => state.branchOffice.branchOffices);
  const realEstate = useSelector(state => state.realEstate.realEstate);
  const neighborhoodsObj = useSelector(state => state.props.neighborhoodsObj);

  const [filteredConversations, setFilteredConversations] = useState([]);
  const [searchConversations, searchInConversations] = useState('');

  const { conversations, conversation, loading, conversationsError } =
    useSelector(state => state.chat);

  useEffect(() => {
    userLogged?.id && dispatch(getBranchOfficesByUserId(userLogged.id));
    userLogged?.id && dispatch(getRealEstateByUserId(userLogged.id));
  }, [userLogged]);

  useEffect(() => {
    if (branchOffices.length === 0) return;
    const branchOfficesIds = branchOffices.map(branchOffice => branchOffice.id);

    dispatch(getConversations(branchOfficesIds));

    return () => {
      socket &&
        socket.on('message', () => {
          dispatch(getConversations(branchOfficesIds));
        });
    };
  }, [branchOffices]);

  useEffect(() => {
    if (conversations.length === 0) return;
    setFilteredConversations(
      conversations.filter(conversation => {
        const data = `${conversation.client?.firstName} ${conversation.client?.lastName} ${conversation.client?.email} ${conversation.lastMessage?.message}`;

        return data.toLowerCase().includes(searchConversations.toLowerCase());
      })
    );
  }, [conversations, searchConversations]);

  return loading ? (
    <div className="d-flex justify-content-center align-items-center card-chat">
      <Spinner
        animation="border"
        variant="primary"
        style={{
          width: '5rem',
          height: '5rem'
        }}
      />
    </div>
  ) : conversationsError ? (
    <div className="d-flex justify-content-center align-items-center card-chat">
      <h3>{conversationsError}</h3>
    </div>
  ) : (
    <ChatProvider
      conversations={filteredConversations}
      conversation={conversation}
      neighborhoodsObj={neighborhoodsObj}
      socket={socket}
      actions={{
        getConversations: async () =>
          await dispatch(
            getConversations(branchOffices.map(branch => branch.id))
          ),
        getConversation: async (id, where) =>
          await dispatch(getConversation(id, where)),
        sendMessage: async ({ conversationId, message }) =>
          await dispatch(
            sendMessage({ conversationId, message, userId: userLogged.id })
          ),
        readMessages: async conversationId =>
          await dispatch(readMessages(conversationId, true)),
        searchInConversations
      }}
      currentUser={{
        ...realEstate,
        logo: `${process.env.REACT_APP_CLIENT}/uploads/realestate/logo/${realEstate.logo}`
      }}
    >
      <ChatTab cid={cid} />
    </ChatProvider>
  );
};

export default Chat;
